import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMap, useMapEvents, Rectangle } from "react-leaflet";
import {
  getPrecisionByZoom,
  calculateVisibleGeohashes,
  isMobileDevice,
} from "../utils/geohash.utils";
import imageOverlayService from "../services/imageOverlay.service";

/**
 * Rota modu için görünür geohash hücrelerini (Rectangle) çizer.
 * Ana harita artık sadece cluster modunda çalışır; viewport tabanlı klasik overlay stil kaldırıldı.
 */
const GeohashGrid = ({
  isRouteMode,
  routeSelected,
  onGeohashSelect,
  showGrids = true,
  worldGeohashData = null,
}) => {
  const [geohashBounds, setGeohashBounds] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const map = useMap();
  const debounceTimerRef = useRef(null);
  const previousBoundsRef = useRef(null);
  const previousParentGeohashesRef = useRef([]);

  const BUFFER_FACTOR = 0.1;

  useEffect(() => {
    const checkMobile = () => setIsMobile(isMobileDevice());
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    imageOverlayService.setMap(map);
    return () => imageOverlayService.clearAllImageLayers();
  }, [map]);

  const calculateGeohashes = useCallback(async () => {
    try {
      const currentZoom = map.getZoom();
      const precision = getPrecisionByZoom(currentZoom, isMobile);
      imageOverlayService.setCurrentPrecision(precision);

      const bounds = map.getBounds();
      const boundsStr = bounds.toBBoxString();
      if (previousBoundsRef.current === boundsStr) return;
      previousBoundsRef.current = boundsStr;

      const { geohashes, parentGeohashes } = calculateVisibleGeohashes(
        bounds,
        precision,
        BUFFER_FACTOR,
      );
      setGeohashBounds(geohashes);
      imageOverlayService.setCurrentGeofences(parentGeohashes);
      if (parentGeohashes.length === 0) {
        previousParentGeohashesRef.current = [];
      }
    } catch (error) {
      console.error("Geohash hesaplama hatası:", error);
    }
  }, [map, isMobile]);

  const debouncedCalculate = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(calculateGeohashes, 1);
  }, [calculateGeohashes]);

  useMapEvents({
    moveend: debouncedCalculate,
    zoomend: debouncedCalculate,
    resize: debouncedCalculate,
    load: calculateGeohashes,
  });

  useEffect(() => {
    calculateGeohashes();
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [calculateGeohashes]);

  const getGeohashStyle = () => ({
    color: "#0e0e0e",
    weight: 1,
    fillOpacity: 0,
    fillColor: "transparent",
    opacity: 0,
  });

  const handleGeohashClick = (hash) => {
    if (isRouteMode && onGeohashSelect && showGrids) {
      onGeohashSelect(hash, map);
    }
  };

  if (isRouteMode && !showGrids) return null;

  return (
    <>
      {geohashBounds.map((item) => (
        <Rectangle
          key={item.hash}
          bounds={item.bounds}
          pathOptions={getGeohashStyle()}
          eventHandlers={{ click: () => handleGeohashClick(item.hash) }}
          style={{ cursor: isRouteMode ? "pointer" : "default" }}
        />
      ))}
    </>
  );
};

export default GeohashGrid;
